import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Checkbox,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  LinearProgress,
  CircularProgress,
  Collapse,
  Button,
  useTheme,
  useMediaQuery,
  InputAdornment,
  TextField,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Badge,
  Chip,
  Avatar,
  MenuList,
} from '@mui/material';
import {
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowRight as ArrowRightIcon,
  KeyboardArrowLeft as PrevIcon,
  KeyboardArrowRight as NextIcon,
  Attachment as AttachmentIcon,
  MoreVert as MoreVertIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  MarkEmailRead as MarkReadIcon,
  MarkEmailUnread as MarkUnreadIcon,
  Star as StarIcon,
  Flag as FlagIcon,
  DriveFileMove as MoveIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Report as SpamIcon,
  MoveToInbox as InboxIcon,
  MailOutline as MailOutlineIcon,
  CheckCircle as CheckCircleIcon,
  ViewAgenda as ViewAgendaIcon,
  ViewStream as ViewStreamIcon,
  ViewHeadline as ViewHeadlineIcon,
  VerticalSplit as VerticalSplitIcon,
  HorizontalSplit as HorizontalSplitIcon,
  WebAsset as NoSplitIcon,
  Refresh as RefreshIcon,
  Snooze as SnoozeIcon,
  TaskAlt as AddToTasksIcon,
  Label as LabelAsIcon,
  ForwardToInbox as ForwardAttachmentIcon,
  FilterList as FilterMessagesIcon,
  VolumeOff as MuteIcon,
  ChevronRight as ChevronRightIcon,
  Search as SearchIcon,
  Add as AddLabelIcon,
  Settings as ManageLabelsIcon,
  Inbox as InboxCatIcon,
  People as PeopleIcon,
  LocalOffer as PromoIcon,
  Notifications as UpdatesIcon,
  Forum as ForumsIcon,
  Forum as ThreadIcon,
  BookmarkAdd as BookmarkAddIcon,
  StarBorder as StarBorderIcon,
  PushPin as PushPinIcon,
  Schedule as ScheduleIcon,
  Verified as VerifiedIcon,
  ErrorOutline as DeliveryFailedIcon,
  ScheduleSend as DeliveryPendingIcon,
  DoneAll as DeliveryDeliveredIcon,
  ExpandMore as ExpandMoreIcon,
  KeyboardArrowDown as ChevronDownIcon,
} from '@mui/icons-material';
import { VariableSizeList as List, type ListOnItemsRenderedProps } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useMailStore } from '../../stores/mailStore';
import { useThemeStore } from '../../stores/themeStore';
import { useAuthStore } from '../../stores/authStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { isDemoUser } from '../../utils/demoMode';
import EmailDetailView from './EmailDetailView';
import { format, isThisMonth, isThisYear } from 'date-fns';
import { formatEmailDate as formatEmailDateIST, formatEmailDateCompact, formatISTDate } from '../../utils/helpers';

// System MIME types that should not be shown as user attachments
const SYSTEM_MIME_TYPES = ['message/delivery-status', 'message/rfc822', 'text/rfc822-headers', 'application/pgp-signature', 'application/pkcs7-signature', 'application/x-pkcs7-signature', 'message/disposition-notification'];

// Get only real user-attached files (exclude inline, system, auto-generated)
const getRealAttachments = (attachments: any[] | undefined | null) => {
  if (!attachments) return [];
  return attachments.filter((att: any) => {
    if (att.contentDisposition === 'inline') return false;
    if (att.contentId) return false;
    if (SYSTEM_MIME_TYPES.includes(att.contentType?.toLowerCase())) return false;
    if (att.filename === 'attachment' && (!att.contentType || att.contentType.startsWith('message/') || att.contentType.startsWith('text/rfc822'))) return false;
    return true;
  });
};

// Extract plain search term from query string (removes operators like from:, subject:, etc.)
const getPlainSearchTerm = (query: string): string => {
  if (!query) return '';
  return query
    .replace(/\b(?:from|to|tocc|cc|subject|body|contains|folder|label|attachment|after|before|has|is|in|filename):[^\s]*/gi, '')
    .trim();
};

// Highlight matching text in a string, returns ReactNode array
const highlightText = (text: string, term: string): React.ReactNode => {
  if (!term || !text) return text;
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escaped})`, 'gi');
  const parts = text.split(regex);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    regex.test(part)
      ? <mark key={i} style={{ backgroundColor: 'rgba(255, 193, 7, 0.45)', color: 'inherit', borderRadius: '2px', padding: '0 1px', fontWeight: 600 }}>{part}</mark>
      : part
  );
};

// Conversation/Thread group interface
interface EmailThread {
  threadId: string;
  emails: any[];
  latestEmail: any;
  unreadCount: number;
  participantCount: number;
}

// Gmail-style avatar palette (Material colors) — deterministic per sender address
const AVATAR_COLORS = [
  '#e8710a', '#d93025', '#a142f4', '#9334e6', '#188038', '#0d652d',
  '#1a73e8', '#1967d2', '#f29900', '#e37400', '#c5221f', '#7b1fa2',
  '#00695c', '#2e7d32', '#c62828', '#4527a0', '#00838f', '#ef6c00',
];
const avatarColorFor = (seed: string): string => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

// Extract a display name + address from an EmailAddress | string
const parseSender = (from: any): { name: string; address: string } => {
  if (typeof from === 'string') {
    const m = from.match(/^(.*?)\s*<([^>]+)>$/);
    if (m) return { name: m[1].trim().replace(/^"|"$/g, ''), address: m[2] };
    return { name: from, address: from };
  }
  return { name: from?.name || '', address: from?.address || '' };
};

// Initials for the avatar (up to 2 chars)
const getInitials = (name: string, address: string): string => {
  const src = name || address;
  const parts = src.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (src[0] || '?').toUpperCase();
};

// Whether the sender is "verified" (DKIM/SPF authenticated). Falls back to a
// heuristic: known/trusted domains or explicit auth headers.
const isVerifiedSender = (email: any): boolean => {
  const headers = email?.headers || {};
  const auth = (headers['authentication-results'] || headers['received-spf'] || '').toLowerCase();
  if (auth.includes('dkim=pass') || auth.includes('spf=pass')) return true;
  const from = parseSender(email?.from);
  const addr = from.address.toLowerCase();
  // Trusted well-known domains
  const trusted = ['gmail.com', 'google.com', 'outlook.com', 'microsoft.com', 'yahoo.com', 'apple.com', 'amazon.com', 'linkedin.com', 'github.com', 'cruvzmail.com'];
  return trusted.some(d => addr.endsWith('@' + d));
};

// Delivery status icon + color for sent folder
const getDeliveryMeta = (email: any): { icon: React.ReactNode; color: string; label: string } | null => {
  const st = email?.deliveryStatus;
  if (!st || st === 'none') return null;
  if (st === 'delivered') return { icon: <DeliveryDeliveredIcon sx={{ fontSize: 13 }} />, color: '#34a853', label: 'Delivered' };
  if (st === 'failed') return { icon: <DeliveryFailedIcon sx={{ fontSize: 13 }} />, color: '#ea4335', label: 'Failed' };
  if (st === 'pending') return { icon: <DeliveryPendingIcon sx={{ fontSize: 13 }} />, color: '#f9ab00', label: 'Pending' };
  return { icon: <DeliveryDeliveredIcon sx={{ fontSize: 13 }} />, color: '#34a853', label: 'Partial' };
};

// Email Card Component with hover actions
interface EmailCardProps {
  email: any;
  isSelected: boolean;
  isActive: boolean;
  isDark: boolean;
  isSentFolder?: boolean;
  isArchiveFolder?: boolean;
  isMobile?: boolean;
  onSelect: () => void;
  onClick: () => void;
  onArchive: () => void;
  onUnarchive?: () => void;
  onDelete: () => void;
  onStar?: (email: any) => void;
}

// Custom memo comparison: only re-render if the email content or selection state changed
const emailCardAreEqual = (prev: EmailCardProps, next: EmailCardProps) =>
  prev.email.id === next.email.id &&
  prev.email.isRead === next.email.isRead &&
  prev.email.isStarred === next.email.isStarred &&
  prev.email.isFlagged === next.email.isFlagged &&
  prev.isSelected === next.isSelected &&
  prev.isActive === next.isActive &&
  prev.isDark === next.isDark;

const EmailCard: React.FC<EmailCardProps> = React.memo(({
  email,
  isSelected,
  isActive,
  isDark,
  isSentFolder = false,
  isArchiveFolder = false,
  isMobile = false,
  onSelect,
  onClick,
  onArchive,
  onUnarchive,
  onDelete,
  onStar,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { densityMode } = useThemeStore();
  const searchQuery = useMailStore(state => state.searchQuery);
  const { settings: emailSettings } = useSettingsStore();
  const highlightTerm = getPlainSearchTerm(searchQuery);

  // Zoho-like compact density values
  const densityPy = densityMode === 'compact' ? 0.15 : densityMode === 'cozy' ? 0.35 : 0.6;
  const densityMinH = densityMode === 'compact' ? 28 : densityMode === 'cozy' ? 36 : 44;
  const densityFontSize = '14px'; // matches Zoho sender (14px)
  const densitySubFontSize = '14px'; // matches Zoho subject (14px)

  const getSenderDisplay = () => {
    if (isSentFolder) {
      const to = Array.isArray(email.to) ? email.to[0] : email.to;
      const name = typeof to === 'string' ? to : (to?.name || '');
      const addr = typeof to === 'string' ? to : (to?.address || '');
      if (name && name !== addr) return `"${name}"<${addr}>`;
      return addr || 'Unknown';
    }
    return typeof email.from === 'string'
      ? email.from
      : (email.from?.name || email.from?.address || 'Unknown');
  };

  // Extract preview text from email body (plain text or HTML)
  const getPreviewText = (maxLength: number = 100): string => {
    let text = '';
    if (email.textBody) {
      text = email.textBody;
    } else if (email.htmlBody) {
      // Fast regex-based HTML stripping — avoids document.createElement + innerHTML
      // which forces a full DOM parse and can block the main thread for seconds
      // when rendering many emails with large HTML bodies simultaneously.
      text = email.htmlBody
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, ' ')
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ' ')
        .replace(/<[^>]+>/g, ' ');
    }
    // Clean up: remove HTML entities and extra whitespace
    text = text.replace(/&nbsp;/g, ' ').replace(/&[a-z]+;/gi, ' ').replace(/\s+/g, ' ').trim();
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <Box
      onMouseEnter={isMobile ? undefined : () => setIsHovered(true)}
      onMouseLeave={isMobile ? undefined : () => setIsHovered(false)}
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: isMobile ? 1.5 : 1,
        py: isMobile ? 1 : densityPy,
        minHeight: isMobile ? 56 : densityMinH,
        // Scroll performance: skip layout/paint work for rows scrolled out of view.
        // As more pages load via infinite scroll, the DOM keeps growing (rows are never
        // unmounted) -- without this, that growth progressively slows down scrolling on
        // large mailboxes. `content-visibility: auto` lets the browser treat off-screen
        // rows like `display: none` for rendering cost, while `contain-intrinsic-size`
        // reserves the right amount of space so scrollbar size/position stay correct.
        // Native, zero-behavior-change, and safely ignored by browsers that don't support it.
        contentVisibility: 'auto',
        containIntrinsicSize: `auto ${isMobile ? 56 : densityMinH}px`,
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0'}`,
        cursor: 'pointer',
        bgcolor: isActive 
          ? (isDark ? 'rgba(21, 97, 232, 0.18)' : '#dce8f8') 
          : isSelected 
            ? (isDark ? 'rgba(21, 97, 232, 0.12)' : '#e8f0fe') 
            : isHovered
              ? (isDark ? 'rgba(255,255,255,0.07)' : '#f2f6fc')
              : !email.isRead
                ? (isDark ? 'rgba(26, 115, 232, 0.07)' : '#f0f4ff')
                : (isDark ? 'transparent' : 'transparent'),
        borderLeft: isActive ? '3px solid #1a73e8' : !email.isRead ? '3px solid #1a73e8' : '3px solid transparent',
        transition: 'background-color 0.1s ease',
      }}
    >
      {/* Checkbox - hidden on mobile, shows on hover/selected on desktop */}
      {!isMobile && (
        <Checkbox
          size="small"
          checked={isSelected}
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
          sx={{
            p: 0.2,
            mr: 0.5,
            opacity: (isHovered || isSelected) ? 1 : 0.18,
            transition: 'opacity 0.12s',
            color: isDark ? '#666' : '#5f6368',
            '&.Mui-checked': { color: '#1a73e8' },
            '& .MuiSvgIcon-root': { fontSize: 16 },
            '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(26,115,232,0.08)' },
            borderRadius: '4px',
          }}
        />
      )}

      {/* Gmail-style circular avatar (colored initials) */}
      <Box
        sx={{
          width: isMobile ? 34 : 30,
          height: isMobile ? 34 : 30,
          flexShrink: 0,
          mr: isMobile ? 1.25 : 1,
          position: 'relative',
        }}
      >
        <Avatar
          sx={{
            width: '100%',
            height: '100%',
            bgcolor: avatarColorFor(parseSender(email.from).address || email.id),
            fontSize: isMobile ? 13 : 12,
            fontWeight: 600,
            color: '#fff',
            fontFamily: '"Lato", "Segoe UI", -apple-system, BlinkMacSystemFont, Arial, sans-serif !important',
          }}
        >
          {getInitials(parseSender(email.from).name, parseSender(email.from).address)}
        </Avatar>
        {/* Unread dot indicator */}
        {!email.isRead && (
          <Box
            sx={{
              position: 'absolute',
              bottom: -1,
              right: -1,
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: '#1a73e8',
              border: '2px solid',
              borderColor: isDark ? '#0d1117' : '#ffffff',
            }}
          />
        )}
      </Box>

      {/* Two-line content area - Gmail layout: Line1=sender, Line2=subject+date */}
      <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
        {/* Line 1: Sender + verified badge + star */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.1 }}>
          <Typography
            sx={{
              fontWeight: email.isRead ? 400 : 700,
              color: isDark ? (email.isRead ? '#7a8390' : '#e8eaed') : (email.isRead ? '#5f6368' : '#0d0d0d'),
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: densityFontSize,
              fontFamily: '"Lato", "Segoe UI", -apple-system, BlinkMacSystemFont, Arial, sans-serif !important',
              textRendering: 'optimizeLegibility',
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            {highlightText(getSenderDisplay(), highlightTerm)}
          </Typography>
          {/* Verified sender badge */}
          {!isSentFolder && isVerifiedSender(email) && (
            <Tooltip title="Verified sender" placement="top">
              <VerifiedIcon sx={{ fontSize: 14, color: '#1a73e8', ml: 0.5, flexShrink: 0 }} />
            </Tooltip>
          )}
          {/* Star flag (Gmail-style) */}
          <Box
            onClick={(e) => { e.stopPropagation(); onStar && onStar(email); }}
            sx={{
              ml: 0.5,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: email.isStarred ? '#f4b400' : (isHovered ? (isDark ? '#6b7280' : '#b0b3b8') : 'transparent'),
              '&:hover': { color: '#f4b400' },
              transition: 'color 0.12s',
            }}
          >
            {email.isStarred ? <StarIcon sx={{ fontSize: 16 }} /> : <StarBorderIcon sx={{ fontSize: 16 }} />}
          </Box>
        </Box>

        {/* Line 2: [attachment_icon] Subject ... [att_size] [green_check] [date] | [hover icons] */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {getRealAttachments(email.attachments).length > 0 && (
            <AttachmentIcon sx={{ fontSize: 13, color: isDark ? '#666' : '#999', mr: 0.5, flexShrink: 0 }} />
          )}
          <Typography
            sx={{
              fontWeight: email.isRead ? 400 : 600,
              color: isDark ? (email.isRead ? '#6b7280' : '#d1d5db') : (email.isRead ? '#6b7280' : '#1a1a1a'),
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: densitySubFontSize,
              textRendering: 'optimizeLegibility',
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            {highlightText(email.subject || '(no subject)', highlightTerm)}
          </Typography>

          {/* Right side of line 2 — Gmail-style: date always visible, hover icons overlay on top */}
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', ml: 1, flexShrink: 0, gap: 0.5 }}>
            {/* Attachment count badge (Gmail-style) */}
            {(() => {
              const ra = getRealAttachments(email.attachments);
              return ra.length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 18,
                    height: 18,
                    px: 0.5,
                    bgcolor: '#d3e3fd',
                    color: '#1967d2',
                    borderRadius: '3px',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                  }}
                >
                  {ra.length}
                </Box>
              ) : null;
            })()}
            {isSentFolder && (
              <CheckCircleIcon sx={{ fontSize: 14, color: '#34a853' }} />
            )}
            {/* Delivery status on sent (Zoho-style) */}
            {isSentFolder && (() => {
              const dm = getDeliveryMeta(email);
              return dm ? (
                <Tooltip title={dm.label} placement="top">
                  <Box sx={{ display: 'flex', alignItems: 'center', color: dm.color }}>{dm.icon}</Box>
                </Tooltip>
              ) : null;
            })()}
            {/* Pinned indicator */}
            {email.isPinned && (
              <PushPinIcon sx={{ fontSize: 13, color: isDark ? '#9ca3af' : '#5f6368', transform: 'rotate(45deg)' }} />
            )}
            {/* Flagged indicator (compact list design) — small colored square */}
            {email.isFlagged && (
              <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: '#f4c20d', flexShrink: 0 }} />
            )}
            <Typography
              sx={{ 
                color: isDark ? '#777' : '#5f6368', 
                fontSize: '10px', // matches Zoho date
                fontWeight: 400,
                whiteSpace: 'nowrap',
              }}
            >
              {formatEmailDateCompact(email.receivedAt || email.date || email.createdAt)}
            </Typography>

            {/* Hover action icons — absolutely positioned over the date so layout never shifts */}
            {!isMobile && (
              <Box
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                  bgcolor: isDark ? 'rgba(32,33,36,0.95)' : 'rgba(255,255,255,0.95)',
                  borderRadius: '4px',
                  opacity: isHovered ? 1 : 0,
                  pointerEvents: isHovered ? 'auto' : 'none',
                  transition: 'opacity 0.12s ease',
                  px: 0.25,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {isArchiveFolder ? (
                  <Tooltip title="Move to Inbox" placement="top">
                    <IconButton size="small" onClick={onUnarchive} sx={{ p: 0.25, '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#eee' } }}>
                      <InboxIcon sx={{ fontSize: 15, color: isDark ? '#aaa' : '#666' }} />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="Archive" placement="top">
                    <IconButton size="small" onClick={onArchive} sx={{ p: 0.25, '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#eee' } }}>
                      <ArchiveIcon sx={{ fontSize: 15, color: isDark ? '#aaa' : '#666' }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Delete" placement="top">
                  <IconButton size="small" onClick={onDelete} sx={{ p: 0.25, '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#eee' } }}>
                    <DeleteIcon sx={{ fontSize: 15, color: isDark ? '#aaa' : '#666' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>

        {/* Line 3: Preview text (Gmail-style) */}
        {!isMobile && (
          <Box sx={{ mt: 0.25 }}>
            <Typography
              sx={{
                fontSize: '13px', // matches Zoho content
                color: isDark ? '#666' : '#5f6368',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: 400,
              }}
            >
              {highlightText(getPreviewText(120), highlightTerm)}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}, emailCardAreEqual);

// Thread Card Component for conversation view
interface ThreadCardProps {
  thread: EmailThread;
  isSelected: boolean;
  isActive: boolean;
  isExpanded: boolean;
  isDark: boolean;
  isSentFolder?: boolean;
  isArchiveFolder?: boolean;
  isMobile?: boolean;
  onToggleExpand: () => void;
  onSelectThread: () => void;
  onSelectEmail: (email: any) => void;
  onStar: (email: any) => void;
  onArchive: (email: any) => void;
  onUnarchive?: (email: any) => void;
  onDelete: (email: any) => void;
}

// Custom memo for ThreadCard: only re-render if thread content or selection changed
const threadCardAreEqual = (prev: ThreadCardProps, next: ThreadCardProps) =>
  prev.thread.threadId === next.thread.threadId &&
  prev.thread.unreadCount === next.thread.unreadCount &&
  prev.thread.latestEmail.id === next.thread.latestEmail.id &&
  prev.thread.latestEmail.isRead === next.thread.latestEmail.isRead &&
  prev.isSelected === next.isSelected &&
  prev.isActive === next.isActive &&
  prev.isExpanded === next.isExpanded &&
  prev.isDark === next.isDark;

const ThreadCard: React.FC<ThreadCardProps> = React.memo(({
  thread,
  isSelected,
  isActive,
  isExpanded,
  isDark,
  isSentFolder = false,
  isArchiveFolder = false,
  isMobile = false,
  onToggleExpand,
  onSelectThread,
  onSelectEmail,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onStar: _onStar,
  onArchive: _onArchive,
  onUnarchive: _onUnarchive,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onDelete: _onDelete,
}) => {
  const email = thread.latestEmail;
  const [isHovered, setIsHovered] = useState(false);
  const { densityMode } = useThemeStore();
  const searchQuery = useMailStore(state => state.searchQuery);
  const { settings: emailSettings } = useSettingsStore();
  const highlightTerm = getPlainSearchTerm(searchQuery);

  // Zoho-like compact density values
  const densityPy = densityMode === 'compact' ? 0.15 : densityMode === 'cozy' ? 0.35 : 0.6;
  const densityMinH = densityMode === 'compact' ? 28 : densityMode === 'cozy' ? 36 : 44;
  const densityFontSize = '14px'; // matches Zoho sender (14px)
  const densitySubFontSize = '14px'; // matches Zoho subject (14px)

  const getSenderDisplay = () => {
    if (isSentFolder) {
      if (thread.emails.length === 1) {
        // Single sent email - show recipient like Zoho: "Name"<email>
        const tos = Array.isArray(email.to) ? email.to : [email.to];
        const firstTo = tos[0];
        const name = typeof firstTo === 'string' ? firstTo : (firstTo?.name || '');
        const addr = typeof firstTo === 'string' ? firstTo : (firstTo?.address || '');
        if (name && name !== addr) return `"${name}"<${addr}>`;
        return addr || 'Unknown';
      }
      // Multi-email thread: Me... other recipients
      const names: string[] = [];
      const seen = new Set<string>();
      thread.emails.forEach((e: any) => {
        const tos = Array.isArray(e.to) ? e.to : [e.to];
        tos.forEach((t: any) => {
          const n = typeof t === 'string' ? t : (t?.name || t?.address || '');
          if (n && !seen.has(n)) { seen.add(n); names.push(n); }
        });
      });
      return `Me... ${names.join(', ')}` || 'Unknown';
    }
    // Inbox: collect unique sender names
    const names: string[] = [];
    const seen = new Set<string>();
    thread.emails.forEach((e: any) => {
      const name = typeof e.from === 'string' ? e.from : (e.from?.name || e.from?.address || '');
      if (name && !seen.has(name)) { seen.add(name); names.push(name); }
    });
    return names.join('  ') || 'Unknown';
  };

  // Extract preview text from email body (plain text or HTML)
  const getPreviewText = (maxLength: number = 100): string => {
    let text = '';
    if (email.textBody) {
      text = email.textBody;
    } else if (email.htmlBody) {
      // Fast regex-based HTML stripping — avoids document.createElement + innerHTML
      text = email.htmlBody
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, ' ')
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ' ')
        .replace(/<[^>]+>/g, ' ');
    }
    text = text.replace(/&nbsp;/g, ' ').replace(/&[a-z]+;/gi, ' ').replace(/\s+/g, ' ').trim();
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <Box>
      <Box
        onMouseEnter={isMobile ? undefined : () => setIsHovered(true)}
        onMouseLeave={isMobile ? undefined : () => setIsHovered(false)}
        onClick={() => thread.emails.length > 1 ? onToggleExpand() : onSelectEmail(email)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: isMobile ? 1.5 : 1,
          py: isMobile ? 1 : densityPy,
          minHeight: isMobile ? 56 : densityMinH,
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0'}`,
          cursor: 'pointer',
          bgcolor: isActive 
            ? (isDark ? 'rgba(21, 97, 232, 0.18)' : '#dce8f8') 
            : isSelected 
              ? (isDark ? 'rgba(21, 97, 232, 0.12)' : '#e8f0fe') 
              : isHovered
                ? (isDark ? 'rgba(255,255,255,0.07)' : '#f2f6fc')
                : thread.unreadCount > 0 
                  ? (isDark ? 'rgba(255,255,255,0.03)' : '#ffffff') 
                  : (isDark ? 'rgba(255,255,255,0.008)' : '#f8f9fa'),
          borderLeft: isActive ? '3px solid #1a73e8' : thread.unreadCount > 0 ? '3px solid rgba(26,115,232,0.45)' : '3px solid transparent',
          transition: 'background-color 0.1s ease',
        }}
      >
        {/* Checkbox - hidden on mobile, shows on hover/selected on desktop */}
        {!isMobile && (
          <Checkbox
            size="small"
            checked={isSelected}
            onClick={(e) => { e.stopPropagation(); onSelectThread(); }}
            sx={{ 
              p: 0.2, 
              mr: 0.25,
              opacity: (isHovered || isSelected) ? 1 : 0.18,
              transition: 'opacity 0.12s',
              color: isDark ? '#666' : '#5f6368',
              '&.Mui-checked': { color: '#1a73e8' },
              '& .MuiSvgIcon-root': { fontSize: 16 },
              '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(26,115,232,0.08)' },
              borderRadius: '4px',
            }}
          />
        )}

        {/* Expand arrow for threads */}
        {thread.emails.length > 1 ? (
          <IconButton size="small" sx={{ p: 0.1, mr: 0.25, color: isDark ? '#666' : '#bbb' }}>
            {isExpanded ? <ArrowDownIcon sx={{ fontSize: 15 }} /> : <ArrowRightIcon sx={{ fontSize: 15 }} />}
          </IconButton>
        ) : null}

        {/* Gmail-style circular avatar (colored initials) */}
        <Box
          sx={{
            width: isMobile ? 34 : 30,
            height: isMobile ? 34 : 30,
            flexShrink: 0,
            mr: isMobile ? 1.25 : 0.75,
            position: 'relative',
          }}
        >
          <Avatar
            sx={{
              width: '100%',
              height: '100%',
              bgcolor: avatarColorFor(parseSender(email.from).address || thread.threadId),
              fontSize: isMobile ? 13 : 12,
              fontWeight: 600,
              color: '#fff',
              fontFamily: '"Lato", "Segoe UI", -apple-system, BlinkMacSystemFont, Arial, sans-serif !important',
            }}
          >
            {getInitials(parseSender(email.from).name, parseSender(email.from).address)}
          </Avatar>
          {thread.unreadCount > 0 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: -1,
                right: -1,
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: '#1a73e8',
                border: '2px solid',
                borderColor: isDark ? '#0d1117' : '#ffffff',
              }}
            />
          )}
        </Box>

        {/* Two-line content area - Gmail layout: Line1=sender, Line2=count+subject+date */}
        <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
          {/* Line 1: Sender + verified badge + star */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.1 }}>
            <Typography
              sx={{
                fontWeight: thread.unreadCount > 0 ? 600 : 500,
                color: isDark ? (thread.unreadCount > 0 ? '#e0e0e0' : '#999') : (thread.unreadCount > 0 ? '#0d0d0d' : '#2d2d2d'),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: densityFontSize,
                fontFamily: '"Lato", "Segoe UI", -apple-system, BlinkMacSystemFont, Arial, sans-serif !important',
                textRendering: 'optimizeLegibility',
                flexGrow: 1,
                minWidth: 0,
              }}
            >
              {highlightText(getSenderDisplay(), highlightTerm)}
            </Typography>
            {/* Verified sender badge */}
            {!isSentFolder && isVerifiedSender(email) && (
              <Tooltip title="Verified sender" placement="top">
                <VerifiedIcon sx={{ fontSize: 14, color: '#1a73e8', ml: 0.5, flexShrink: 0 }} />
              </Tooltip>
            )}
            {/* Star flag (Gmail-style) */}
            <Box
              onClick={(e) => { e.stopPropagation(); _onStar(email); }}
              sx={{
                ml: 0.5,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                color: email.isStarred ? '#f4b400' : (isHovered ? (isDark ? '#6b7280' : '#b0b3b8') : 'transparent'),
                '&:hover': { color: '#f4b400' },
                transition: 'color 0.12s',
              }}
            >
              {email.isStarred ? <StarIcon sx={{ fontSize: 16 }} /> : <StarBorderIcon sx={{ fontSize: 16 }} />}
            </Box>
          </Box>

          {/* Line 2: [thread_count] [attachment_icon] subject ... [att_size] [green_check] [date] | [hover icons] */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {(() => {
              // Show thread count: backend-provided threadCount (search mode) OR local grouping count
              const tc = (thread.latestEmail as any).threadCount ?? thread.emails.length;
              return tc > 1 ? (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                    mr: 0.5,
                    px: 0.5,
                    height: 16,
                    bgcolor: isDark ? 'rgba(96,165,250,0.15)' : '#e8f0fe',
                    borderRadius: '8px',
                    flexShrink: 0,
                  }}
                >
                  <ThreadIcon sx={{ fontSize: 10, color: isDark ? '#60a5fa' : '#1a73e8' }} />
                  <Typography
                    component="span"
                    sx={{ fontSize: '0.65rem', fontWeight: 700, color: isDark ? '#60a5fa' : '#1a73e8', lineHeight: 1 }}
                  >
                    {tc}
                  </Typography>
                </Box>
              ) : null;
            })()}
            {getRealAttachments(email.attachments).length > 0 && (
              <AttachmentIcon sx={{ fontSize: 13, color: isDark ? '#666' : '#999', mr: 0.5, flexShrink: 0 }} />
            )}
            <Typography
              sx={{
                fontWeight: thread.unreadCount > 0 ? 500 : 450,
                color: isDark ? (thread.unreadCount > 0 ? '#ccc' : '#888') : (thread.unreadCount > 0 ? '#1a1a1a' : '#444'),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: densitySubFontSize,
                textRendering: 'optimizeLegibility',
                flexGrow: 1,
                minWidth: 0,
              }}
            >
              {highlightText(email.subject || '(no subject)', highlightTerm)}
            </Typography>

            {/* Right side of line 2 — Gmail-style: date always visible, hover icons overlay on top */}
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', ml: 1, flexShrink: 0, gap: 0.5 }}>
              {/* Attachment count badge (Gmail-style) */}
              {(() => {
                const ra = getRealAttachments(email.attachments);
                return ra.length > 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 18,
                      height: 18,
                      px: 0.5,
                      bgcolor: '#d3e3fd',
                      color: '#1967d2',
                      borderRadius: '3px',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                    }}
                  >
                    {ra.length}
                  </Box>
                ) : null;
              })()}
              {isSentFolder && (
                <CheckCircleIcon sx={{ fontSize: 14, color: '#34a853' }} />
              )}
              {/* Delivery status on sent (Zoho-style) */}
              {isSentFolder && (() => {
                const dm = getDeliveryMeta(email);
                return dm ? (
                  <Tooltip title={dm.label} placement="top">
                    <Box sx={{ display: 'flex', alignItems: 'center', color: dm.color }}>{dm.icon}</Box>
                  </Tooltip>
                ) : null;
              })()}
              {/* Pinned indicator */}
              {email.isPinned && (
                <PushPinIcon sx={{ fontSize: 13, color: isDark ? '#9ca3af' : '#5f6368', transform: 'rotate(45deg)' }} />
              )}
              {/* Flagged indicator (compact list design) — small colored square */}
              {email.isFlagged && (
                <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: '#f4c20d', flexShrink: 0 }} />
              )}
              <Typography
                sx={{ 
                  color: isDark ? '#777' : '#5f6368', 
                  fontSize: '10px', // matches Zoho date
                  fontWeight: 400,
                  whiteSpace: 'nowrap',
                }}
              >
                {formatEmailDateCompact(email.receivedAt || email.date || email.createdAt)}
              </Typography>

              {/* Hover action icons — absolutely positioned over the date so layout never shifts */}
              {!isMobile && (
                <Box
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                    bgcolor: isDark ? 'rgba(32,33,36,0.95)' : 'rgba(255,255,255,0.95)',
                    borderRadius: '4px',
                    opacity: isHovered ? 1 : 0,
                    pointerEvents: isHovered ? 'auto' : 'none',
                    transition: 'opacity 0.12s ease',
                    px: 0.25,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {isArchiveFolder ? (
                    <Tooltip title="Move to Inbox" placement="top">
                      <IconButton size="small" onClick={() => _onUnarchive && _onUnarchive(email)} sx={{ p: 0.25, '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#eee' } }}>
                        <InboxIcon sx={{ fontSize: 15, color: isDark ? '#aaa' : '#666' }} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Archive" placement="top">
                      <IconButton size="small" onClick={() => _onArchive(email)} sx={{ p: 0.25, '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#eee' } }}>
                        <ArchiveIcon sx={{ fontSize: 15, color: isDark ? '#aaa' : '#666' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Delete" placement="top">
                    <IconButton size="small" onClick={() => _onDelete(email)} sx={{ p: 0.25, '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#eee' } }}>
                      <DeleteIcon sx={{ fontSize: 15, color: isDark ? '#aaa' : '#666' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </Box>

          {/* Line 3: Preview text (Gmail-style) */}
          {!isMobile && (
            <Box sx={{ mt: 0.25 }}>
              <Typography
                sx={{
                  fontSize: '13px', // matches Zoho content
                  color: isDark ? '#666' : '#5f6368',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: 400,
                }}
              >
                {highlightText(getPreviewText(120), highlightTerm)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Expanded thread emails */}
      <Collapse in={isExpanded}>
        <Box sx={{ pl: 3.5, bgcolor: isDark ? 'rgba(0,0,0,0.15)' : '#f8f9fa', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.03)' : '#f0f0f0'}` }}>
          {thread.emails.map((threadEmail, index) => (
            <Box
              key={threadEmail.id}
              onClick={() => onSelectEmail(threadEmail)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1.25,
                py: 0.3,
                cursor: 'pointer',
                borderTop: index > 0 ? `1px solid ${isDark ? 'rgba(255,255,255,0.03)' : '#f0f0f0'}` : 'none',
                bgcolor: 'transparent',
                '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#e8f0fe' },
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: threadEmail.isRead ? 'transparent' : '#1561e8',
                  border: threadEmail.isRead ? `1px solid ${isDark ? '#555' : '#dadce0'}` : 'none',
                  mr: 1,
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ width: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mr: 2, fontSize: '0.8rem', fontWeight: 500, letterSpacing: '-0.01em', WebkitFontSmoothing: 'antialiased', textRendering: 'optimizeLegibility', color: isDark ? '#ccc' : '#0d0d0d' }}>
                {typeof threadEmail.from === 'string' ? threadEmail.from : (threadEmail.from?.name || threadEmail.from?.address)}
              </Typography>
              <Typography sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.78rem', color: isDark ? '#888' : '#888' }}>
                {threadEmail.textBody?.substring(0, 80) || ''}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: isDark ? '#777' : '#999', whiteSpace: 'nowrap', ml: 1 }}>
                {new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', month: 'short', day: 'numeric' }).format(new Date(threadEmail.receivedAt || threadEmail.date))}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}, threadCardAreEqual);

// PERF FIX 2026-08-07 (Gap #1b - large mailbox scroll/click slowness): list virtualization.
// Without this, EmailListView mounted every loaded email/thread as a live DOM row
// simultaneously (never unmounting off-screen rows as infinite scroll accumulates more
// pages) -- at ~150+ rows this was measured to make the browser tab itself unresponsive
// for 10s-138s at a time (confirmed via live Playwright network-timing capture), because
// React reconciliation + browser layout/paint cost scales with total mounted DOM nodes,
// not just visible ones. This flattens the date-grouped list into a single virtual row
// list (react-window) so only the visible rows (+ a small overscan buffer) are ever
// mounted, regardless of how many emails have been loaded.
//
// Row heights are not hardcoded/guessed -- each row measures its own real rendered height
// via ResizeObserver and reports it back, so visual output (density modes, mobile sizing,
// wrapped/ellipsis text) is byte-for-byte identical to the pre-virtualization layout.
// Known, disclosed trade-off: native `position: sticky` date-group headers no longer stick
// while scrolling past them (react-window renders discrete absolutely-positioned rows) --
// mitigated below with a lightweight synthetic sticky header bar tracking the topmost
// visible group label.
type VirtualRow =
  | { type: 'header'; key: string; label: string }
  | { type: 'thread'; key: string; thread: EmailThread }
  | { type: 'email'; key: string; email: any }
  | { type: 'loading'; key: string };

interface VirtualRowData {
  rows: VirtualRow[];
  isDark: boolean;
  isMobile: boolean;
  isSentFolder: boolean;
  isArchiveFolder: boolean;
  selectedEmails: string[];
  selectedEmailId: string | undefined;
  expandedThreads: Set<string>;
  onToggleExpand: (threadId: string) => void;
  onSelectEmail: (email: any) => void;
  onToggleEmailSelection: (id: string) => void;
  onArchiveEmail: (email: any) => void;
  onUnarchiveEmail: (email: any) => void;
  onDeleteEmail: (email: any) => void;
  onSelectThread: (thread: EmailThread) => void;
  onStarEmail: (email: any) => void;
  reportRowHeight: (index: number, key: string, height: number) => void;
  clearRowHeight: (index: number, key: string) => void;
}

const HEADER_ROW_HEIGHT = 32;
const LOADING_ROW_HEIGHT = 52;

// Custom scrollable container for react-window's List (replaces the old Box's
// '&::-webkit-scrollbar' sx overrides, which only applied to the outer Box -- react-window
// renders its own internal scroll container, so the same thin/subtle scrollbar styling is
// re-applied here via the modern `scrollbar-width`/`scrollbar-color` CSS properties).
const VirtualScrollOuterElement = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & { isDark?: boolean }>(
  ({ isDark, style, ...rest }, ref) => (
    <div
      ref={ref}
      {...rest}
      style={{
        ...style,
        scrollbarWidth: 'thin',
        scrollbarColor: isDark ? 'rgba(255,255,255,0.12) transparent' : 'rgba(0,0,0,0.1) transparent',
      }}
    />
  )
);
VirtualScrollOuterElement.displayName = 'VirtualScrollOuterElement';

const VirtualizedRow: React.FC<{ index: number; style: React.CSSProperties; data: VirtualRowData }> = ({ index, style, data }) => {
  const row = data.rows[index];
  const measureRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!row) return;
    // PERF FIX 2026-08-08 (Gap: intermittent blank space / jank while scrolling): plain
    // email rows and COLLAPSED threads always render exactly one no-wrap line each for
    // sender/subject (see EmailCard/ThreadCard -- `whiteSpace: 'nowrap'` + ellipsis, never
    // multi-line), so their real height is always exactly what getEstimatedRowHeight()
    // already predicts -- runtime measurement can never disagree with it. Previously EVERY
    // newly-mounted row (i.e. on every scroll) ran a ResizeObserver measurement that could
    // call `resetAfterIndex`, forcing react-window to recompute offsets for the rest of the
    // list -- unnecessary work/churn for rows whose height is provably constant. Only an
    // EXPANDED thread (multiple stacked emails, genuinely variable height) still needs
    // real measurement, plus the header/loading rows (cheap, low-volume).
    const isExpandedThread = row.type === 'thread' && data.expandedThreads.has((row as any).thread?.threadId);
    // GAP FIX 2026-11-03: when a thread is COLLAPSED, its cached height (measured while
    // expanded, e.g. 200px) must be cleared so react-window falls back to the correct
    // estimated height. Otherwise the stale large height is kept and a visible gap appears
    // below the collapsed thread. Only clear when it was previously expanded (has a cached
    // height) to avoid churn on every scroll mount.
    if (row.type === 'thread' && !isExpandedThread) {
      data.clearRowHeight(index, row.key);
      return;
    }
    const needsMeasurement = row.type === 'header' || row.type === 'loading' || isExpandedThread;
    if (!needsMeasurement) return;
    const el = measureRef.current;
    if (!el) return;
    const measure = () => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) data.reportRowHeight(index, row.key, h);
    };
    measure();
    // ResizeObserver catches height changes from thread expand/collapse or content reflow,
    // not just the initial measurement.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.key, row?.type === 'thread' ? data.expandedThreads.has((row as any).thread?.threadId) : null]);


  if (!row) return null;

  if (row.type === 'header') {
    return (
      <div style={style}>
        <Box
          ref={measureRef}
          sx={{
            px: 2,
            py: 0.75,
            bgcolor: data.isDark ? '#0f172a' : '#f8fafc',
            borderBottom: `1px solid ${data.isDark ? '#334155' : '#e2e8f0'}`,
          }}
        >
          <Typography
            sx={{
              color: data.isDark ? '#94a3b8' : '#64748b',
              fontWeight: 600,
              fontSize: '0.75rem',
              letterSpacing: '0.3px',
            }}
          >
            {row.label}
          </Typography>
        </Box>
      </div>
    );
  }

  if (row.type === 'loading') {
    return (
      <div style={style}>
        <Box ref={measureRef} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
          <CircularProgress size={20} sx={{ color: data.isDark ? '#60a5fa' : '#1a73e8' }} />
          <Typography sx={{ ml: 1.5, fontSize: '0.8rem', color: data.isDark ? '#94a3b8' : '#5f6368' }}>
            Loading more emails...
          </Typography>
        </Box>
      </div>
    );
  }

  if (row.type === 'thread') {
    const { thread } = row;
    return (
      <div style={style}>
        <div ref={measureRef}>
          <ThreadCard
            thread={thread}
            isSelected={thread.emails.some(e => data.selectedEmails.includes(e.id))}
            isActive={thread.emails.some(e => data.selectedEmailId === e.id)}
            isExpanded={data.expandedThreads.has(thread.threadId)}
            isDark={data.isDark}
            isSentFolder={data.isSentFolder}
            isArchiveFolder={data.isArchiveFolder}
            isMobile={data.isMobile}
            onToggleExpand={() => data.onToggleExpand(thread.threadId)}
            onSelectThread={() => data.onSelectThread(thread)}
            onSelectEmail={(email) => data.onSelectEmail(email)}
            onStar={(email) => data.onStarEmail(email)}
            onArchive={(email) => data.onArchiveEmail(email)}
            onUnarchive={(email) => data.onUnarchiveEmail(email)}
            onDelete={(email) => data.onDeleteEmail(email)}
          />
        </div>
      </div>
    );
  }

  // email
  const { email } = row;
  return (
    <div style={style}>
      <div ref={measureRef}>
        <EmailCard
          email={email}
          isSelected={data.selectedEmails.includes(email.id)}
          isActive={data.selectedEmailId === email.id}
          isDark={data.isDark}
          isSentFolder={data.isSentFolder}
          isArchiveFolder={data.isArchiveFolder}
          isMobile={data.isMobile}
          onSelect={() => data.onToggleEmailSelection(email.id)}
          onClick={() => data.onSelectEmail(email)}
          onArchive={() => data.onArchiveEmail(email)}
          onUnarchive={() => data.onUnarchiveEmail(email)}
          onDelete={() => data.onDeleteEmail(email)}
          onStar={(e) => data.onStarEmail(e)}
        />
      </div>
    </div>
  );
};

// Search scope/sort bar — rendered inside email list area (in-flow, no overlap issues)
const FILTER_OPERATORS = [
  { op: 'from',     label: 'from:',             desc: 'Sender',          hasValue: true },
  { op: 'to',       label: 'to:',               desc: 'Recipient',       hasValue: true },
  { op: 'subject',  label: 'subject:',           desc: 'Subject',         hasValue: true },
  { op: 'contains', label: 'contains:',          desc: 'Body text',       hasValue: true },
  { op: 'has',      label: 'with attachment',    desc: 'Has attachment',  hasValue: false, preset: 'has:attachment' },
  { op: 'is',       label: 'unread only',        desc: 'Unread',          hasValue: false, preset: 'is:unread' },
  { op: 'after',    label: 'from date:',         desc: 'After date',      hasValue: true },
  { op: 'before',   label: 'to date:',           desc: 'Before date',     hasValue: true },
  { op: 'filename', label: 'attachment name:',   desc: 'Attachment name', hasValue: true },
] as const;

const SearchScopeBar: React.FC<{ currentFolder: string; searchQuery: string }> = ({ currentFolder, searchQuery }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { searchScope, setSearchScope, searchSort, setSearchSort, includeSpamTrash, setIncludeSpamTrash, setSearchQuery, createSavedSearch, fetchSavedSearches } = useMailStore();
  const [saved, setSaved] = useState(false);
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);
  const [pendingOp, setPendingOp] = useState<string | null>(null); // 'from', 'to', etc.
  const [pendingValue, setPendingValue] = useState('');

  const handleSave = async () => {
    try {
      await createSavedSearch({ name: searchQuery, query: searchQuery });
      fetchSavedSearches();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {}
  };

  const applyFilter = (opDef: typeof FILTER_OPERATORS[number], value?: string) => {
    const addend = 'preset' in opDef && opDef.preset
      ? opDef.preset
      : `${opDef.op}:${(value || '').trim()}`;
    if (!addend.trim()) return;
    const newQuery = searchQuery ? `${searchQuery} ${addend}` : addend;
    setSearchQuery(newQuery);
    setFilterAnchor(null);
    setPendingOp(null);
    setPendingValue('');
  };

  return (
    <Box sx={{
      display: 'flex',
      gap: 0.5,
      px: 1.5,
      py: 0.75,
      alignItems: 'center',
      flexWrap: 'nowrap',
      overflowX: 'auto',
      '&::-webkit-scrollbar': { display: 'none' },
      scrollbarWidth: 'none',
      bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#f8f9fa',
      borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e8eaed'}`,
    }}>
      <Chip
        label="All Mail"
        size="small"
        onClick={() => setSearchScope('all')}
        sx={{ height: 22, fontSize: '0.72rem', flexShrink: 0, bgcolor: searchScope === 'all' ? '#1a73e8' : (isDark ? '#2d3748' : '#f1f3f4'), color: searchScope === 'all' ? '#fff' : (isDark ? '#9ca3af' : '#5f6368'), fontWeight: searchScope === 'all' ? 600 : 400, cursor: 'pointer' }}
      />
      <Chip
        label="This Folder"
        size="small"
        onClick={() => setSearchScope('folder')}
        sx={{ height: 22, fontSize: '0.72rem', flexShrink: 0, bgcolor: searchScope === 'folder' ? '#1a73e8' : (isDark ? '#2d3748' : '#f1f3f4'), color: searchScope === 'folder' ? '#fff' : (isDark ? '#9ca3af' : '#5f6368'), fontWeight: searchScope === 'folder' ? 600 : 400, cursor: 'pointer' }}
      />
      <Box sx={{ width: '1px', height: 16, flexShrink: 0, bgcolor: isDark ? 'rgba(255,255,255,0.15)' : '#e0e0e0', mx: 0.25 }} />
      <Chip
        label="By Date"
        size="small"
        onClick={() => setSearchSort('date')}
        sx={{ height: 22, fontSize: '0.72rem', flexShrink: 0, bgcolor: searchSort === 'date' ? '#34a853' : (isDark ? '#2d3748' : '#f1f3f4'), color: searchSort === 'date' ? '#fff' : (isDark ? '#9ca3af' : '#5f6368'), fontWeight: searchSort === 'date' ? 600 : 400, cursor: 'pointer' }}
      />
      <Chip
        label="By Relevance"
        size="small"
        onClick={() => setSearchSort('relevance')}
        sx={{ height: 22, fontSize: '0.72rem', flexShrink: 0, bgcolor: searchSort === 'relevance' ? '#34a853' : (isDark ? '#2d3748' : '#f1f3f4'), color: searchSort === 'relevance' ? '#fff' : (isDark ? '#9ca3af' : '#5f6368'), fontWeight: searchSort === 'relevance' ? 600 : 400, cursor: 'pointer' }}
      />
      <Box sx={{ width: '1px', height: 16, flexShrink: 0, bgcolor: isDark ? 'rgba(255,255,255,0.15)' : '#e0e0e0', mx: 0.25 }} />

      {/* + Add filter button */}
      <Chip
        label="+ Add filter"
        size="small"
        onClick={(e) => { setFilterAnchor(e.currentTarget); setPendingOp(null); setPendingValue(''); }}
        sx={{ height: 22, fontSize: '0.72rem', flexShrink: 0, bgcolor: isDark ? '#2d3748' : '#e8f0fe', color: '#1a73e8', fontWeight: 500, cursor: 'pointer', border: '1px dashed #c5cae9', '&:hover': { bgcolor: '#d2e3fc' } }}
      />
      <Menu
        anchorEl={filterAnchor}
        open={Boolean(filterAnchor)}
        onClose={() => { setFilterAnchor(null); setPendingOp(null); setPendingValue(''); }}
        PaperProps={{ sx: { borderRadius: '10px', minWidth: 300, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', py: 0.5 } }}
      >
        {!pendingOp ? (
          FILTER_OPERATORS.map((opDef) => (
            <MenuItem
              key={opDef.op}
              onClick={() => {
                if (!opDef.hasValue) { applyFilter(opDef); }
                else { setPendingOp(opDef.op); setPendingValue(''); }
              }}
              sx={{ py: 0.75, gap: 2, display: 'flex', alignItems: 'center' }}
            >
              <Typography sx={{ fontSize: '0.85rem', color: '#1a73e8', fontWeight: 600, minWidth: 130 }}>{opDef.label}</Typography>
              <Typography sx={{ fontSize: '0.8rem', color: '#5f6368' }}>{opDef.desc}</Typography>
            </MenuItem>
          ))
        ) : (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography sx={{ fontSize: '0.78rem', color: '#80868b', mb: 1 }}>
              Enter value for <strong style={{ color: '#1a73e8' }}>{pendingOp}:</strong>
            </Typography>
            <input
              autoFocus
              value={pendingValue}
              onChange={(e) => setPendingValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const opDef = FILTER_OPERATORS.find(o => o.op === pendingOp);
                  if (opDef && pendingValue.trim()) applyFilter(opDef, pendingValue);
                }
                if (e.key === 'Escape') { setPendingOp(null); setPendingValue(''); }
              }}
              placeholder={`Type ${pendingOp} value and press Enter`}
              style={{
                width: '100%', border: '1px solid #c5cae9', borderRadius: 6,
                padding: '6px 10px', fontSize: '0.875rem', outline: 'none',
                fontFamily: 'inherit', boxSizing: 'border-box',
              }}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Chip label="Apply" size="small" onClick={() => {
                const opDef = FILTER_OPERATORS.find(o => o.op === pendingOp);
                if (opDef && pendingValue.trim()) applyFilter(opDef, pendingValue);
              }} sx={{ bgcolor: '#1a73e8', color: '#fff', cursor: 'pointer', fontSize: '0.75rem', '&:hover': { bgcolor: '#1557b0' } }} />
              <Chip label="Back" size="small" onClick={() => { setPendingOp(null); setPendingValue(''); }}
                sx={{ cursor: 'pointer', fontSize: '0.75rem' }} />
            </Box>
          </Box>
        )}
      </Menu>

      {/* Include Spam/Trash toggle */}
      <Chip
        label={includeSpamTrash ? '✓ Spam/Trash' : 'Spam/Trash'}
        size="small"
        onClick={() => setIncludeSpamTrash(!includeSpamTrash)}
        sx={{ height: 22, fontSize: '0.72rem', flexShrink: 0, bgcolor: includeSpamTrash ? '#fbbc04' : (isDark ? '#2d3748' : '#f1f3f4'), color: includeSpamTrash ? '#202124' : (isDark ? '#9ca3af' : '#5f6368'), fontWeight: includeSpamTrash ? 600 : 400, cursor: 'pointer' }}
      />

      {/* Save search bookmark */}
      <Tooltip title={saved ? 'Saved!' : 'Save this search'}>
        <IconButton size="small" onClick={handleSave} sx={{ flexShrink: 0, p: 0.25, color: saved ? '#34a853' : (isDark ? '#6b7280' : '#9aa0a6'), '&:hover': { color: '#1a73e8' } }}>
          <BookmarkAddIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// Main Email List View Component
const EmailListView: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));        // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg')); // 600–1200px
  const { folder = 'inbox' } = useParams<{ folder: string }>();
  const {
    emails,
    selectedEmail,
    selectedEmails,
    isLoading,
    fetchEmails,
    fetchEmailsSilent,
    fetchEmail,
    syncEmails,
    setSelectedEmail,
    toggleEmailSelection,
    selectAllEmails,
    clearSelection,
    markAsRead,
    markAsStarred,
    markAsImportant,
    moveToFolder,
    markAsSpam,
    markAsNotSpam,
    deleteEmails,
    currentFolder,
    total,
    page,
    limit,
    hasMore,
    isLoadingMore,
    nextPage,
    prevPage,
    loadMoreEmails,
    searchQuery,
    setSearchQuery,
    setSearchFilters,
    clearSearch,
    inboxCategory,
    setInboxCategory,
    // Scheduled email functions
    scheduledEmails,
    fetchScheduledEmails,
    cancelScheduledEmail,
    loadDemoEmails,
    folders: storeFolders,
    labels,
    addLabel,
    removeLabel,
  } = useMailStore();
  const { readingPanePosition, setReadingPanePosition, densityMode, setDensityMode } = useThemeStore();
  const { user } = useAuthStore();
  const isDemo = isDemoUser(user?.email);

  // Helper function to get date group label
  const getDateGroupLabel = (dateStr: string | undefined) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    const todayLabel = formatEmailDateIST(date);
    if (todayLabel !== 'Yesterday' && !/\d/.test(todayLabel) === false && /^\d/.test(todayLabel) === false) {
      // simple check: if result is a time string (contains ':') it's today
    }
    const key = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
    const nowKey = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
    const yesterdayDate = new Date(); yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    const yesterdayKey = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).format(yesterdayDate);
    if (key === nowKey) return 'Today';
    if (key === yesterdayKey) return 'Yesterday';
    if (isThisMonth(date)) return `Earlier in ${formatISTDate(date, { month: 'long' })}`;
    if (isThisYear(date)) return formatISTDate(date, { month: 'long' });
    return formatISTDate(date, { month: 'long', year: 'numeric' });
  };

  // Group emails by date for display
  const groupedEmails = useMemo(() => {
    const groups: { label: string; emails: any[] }[] = [];
    let currentGroup = '';
    
    emails.forEach(email => {
      const dateStr = email.receivedAt || email.date || email.createdAt;
      const groupLabel = getDateGroupLabel(dateStr);
      
      if (groupLabel !== currentGroup) {
        currentGroup = groupLabel;
        groups.push({ label: groupLabel, emails: [email] });
      } else if (groups.length > 0) {
        groups[groups.length - 1].emails.push(email);
      }
    });
    
    return groups;
  }, [emails]);

  // For scheduled folder, convert scheduled emails to display format
  const isScheduledFolder = folder === 'scheduled';
  const displayEmails = useMemo(() => {
    if (isScheduledFolder) {
      return scheduledEmails
        .filter(s => s.status === 'pending')
        .map(s => ({
          id: s.id,
          messageId: s.id,
          threadId: s.id,
          subject: s.subject,
          from: { address: s.fromAddress },
          to: s.toAddresses.map(a => ({ address: a })),
          body: s.htmlBody || s.textBody || '',
          htmlBody: s.htmlBody,
          textBody: s.textBody,
          date: s.scheduledAt,
          scheduledAt: s.scheduledAt,
          isScheduled: true,
          status: s.status,
          isRead: false,
          isStarred: false,
          isFlagged: false,
          isImportant: false,
          isDraft: false,
          labels: [] as string[],
          folder: 'scheduled',
          attachments: s.attachments || [],
        }));
    }
    return emails;
  }, [isScheduledFolder, scheduledEmails, emails]);

  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  const [moveMenuAnchor, setMoveMenuAnchor] = useState<null | HTMLElement>(null);
  const [labelMenuAnchor, setLabelMenuAnchor] = useState<null | HTMLElement>(null);
  const [labelSearch, setLabelSearch] = useState('');
  const { enableThreading } = useMailStore() as any;
  const _enableThreading = typeof enableThreading === 'boolean' ? enableThreading : true;
  const [conversationView, setConversationView] = useState(_enableThreading);
  void setConversationView; // exposed for future toggle UI
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());
  // Track if split was auto-opened on email click (to revert on close)
  const [autoSplitOpened, setAutoSplitOpened] = useState(false);
  const [densityMenuAnchor, setDensityMenuAnchor] = useState<null | HTMLElement>(null);
  const [viewMenuAnchor, setViewMenuAnchor] = useState<null | HTMLElement>(null);

  // Group emails by threadId for conversation view
  // PERF FIX 2026-08-09 (audit: "why does scrolling past 100+ emails get slow", cross-checked
  // against how Gmail/Zoho/Roundcube avoid this class of problem): those clients never
  // re-derive thread groupings/unread-counts/participants for rows that are already rendered
  // just because more rows were appended -- they merge the new page's threads into the
  // existing set. This component previously rebuilt the ENTIRE thread map (Map construction +
  // per-thread sort + participants Set + unreadCount filter, for every thread) from scratch on
  // every loadMoreEmails() append, an O(total accumulated emails) cost repeated on every scroll
  // page -- the deeper the user had scrolled, the more total work each new page triggered.
  // threadCacheRef/threadObjCacheRef persist the already-computed EmailThread objects across
  // renders; a genuine append (new emails tacked onto the end -- detected by the newest/first
  // email being unchanged) only processes the NEW emails and only re-sorts/recomputes the
  // (small number of) threads that actually received a new email this pass. A full rebuild
  // still happens correctly on folder switch, search, category filter, or the 60s silent-poll
  // merge (any case where the first email changed or the list shrank).
  const threadCacheRef = useRef<{ threadMap: Map<string, any[]>; lastEmails: any[] }>({ threadMap: new Map(), lastEmails: [] });
  const threadObjCacheRef = useRef<Map<string, EmailThread>>(new Map());

  const emailThreads = useMemo<EmailThread[]>(() => {
    if (!conversationView || isScheduledFolder) {
      threadCacheRef.current = { threadMap: new Map(), lastEmails: [] };
      threadObjCacheRef.current = new Map();
      return [];
    }

    const cache = threadCacheRef.current;
    const isAppend = cache.lastEmails.length > 0 &&
      displayEmails.length >= cache.lastEmails.length &&
      displayEmails[0]?.id === cache.lastEmails[0]?.id;

    const threadMap = isAppend ? cache.threadMap : new Map<string, any[]>();
    const newEmails = isAppend ? displayEmails.slice(cache.lastEmails.length) : displayEmails;
    const touchedThreadIds = new Set<string>();

    if (!isAppend) threadObjCacheRef.current = new Map();

    newEmails.forEach(email => {
      const threadId = email.threadId || email.id;
      if (!threadMap.has(threadId)) threadMap.set(threadId, []);
      threadMap.get(threadId)!.push(email);
      touchedThreadIds.add(threadId);
    });

    // Recompute (sort + participants + unreadCount) only for threads that changed this pass.
    touchedThreadIds.forEach(threadId => {
      const threadEmails = threadMap.get(threadId)!;
      threadEmails.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      const participants = new Set<string>();
      threadEmails.forEach(e => {
        const addr = typeof e.from === 'string' ? e.from : e.from?.address;
        if (addr) participants.add(addr);
      });

      threadObjCacheRef.current.set(threadId, {
        threadId,
        emails: threadEmails,
        latestEmail: threadEmails[0],
        unreadCount: threadEmails.filter(e => !e.isRead).length,
        participantCount: participants.size,
      });
    });

    cache.threadMap = threadMap;
    cache.lastEmails = displayEmails;

    // Sorting the merged (mostly-cached) set is the one remaining unavoidable cost of
    // presenting a single globally-ordered list -- but it's now over EmailThread objects
    // (cheap comparator, no re-derivation), not over raw emails with re-grouping work attached.
    const threads = Array.from(threadObjCacheRef.current.values());
    threads.sort((a, b) => new Date(b.latestEmail.date).getTime() - new Date(a.latestEmail.date).getTime());
    return threads;
  }, [displayEmails, conversationView, isScheduledFolder]);

  const toggleThreadExpand = (threadId: string) => {
    setExpandedThreads(prev => {
      const next = new Set(prev);
      if (next.has(threadId)) next.delete(threadId);
      else next.add(threadId);
      return next;
    });
  };

  // PERF FIX 2026-08-07 (Gap #1b): flatten the date-grouped list into a single virtual
  // row array consumed by react-window. See VirtualizedRow component above for details.
  // DESIGN CHANGE 2026-08-08 (compact list style): date-group header rows are no longer
  // pushed -- the target compact design is a continuous flat list with no section breaks
  // (each row carries its own full timestamp instead). `groupedEmails` is still computed
  // above (unchanged) since it's also used to derive `emailThreads`/filtering; only the
  // header row itself is omitted here.
  // PERF FIX 2026-08-09: emailThreads is already fully sorted by latest-email date (see
  // above), so re-deriving the same order via a nested groupedEmails.forEach ->
  // emailThreads.filter(dateGroupLabel matches) loop was redundant O(dateGroups × threads)
  // work repeated on every render -- a leftover from before date-group header rows were
  // removed from this view (2026-08-08 compact design change). Iterating emailThreads
  // directly produces the identical order with no nested-loop cost.
  const virtualRows = useMemo<VirtualRow[]>(() => {
    const rows: VirtualRow[] = [];
    if (conversationView) {
      // Date-grouped thread sections (Gmail-style)
      let lastGroup = '';
      emailThreads.forEach(thread => {
        const dateStr = thread.latestEmail.receivedAt || thread.latestEmail.date || thread.latestEmail.createdAt;
        const group = getDateGroupLabel(dateStr);
        if (group !== lastGroup) {
          lastGroup = group;
          rows.push({ type: 'header', key: `hdr-${group}`, label: group });
        }
        rows.push({ type: 'thread', key: thread.threadId, thread });
      });
    } else {
      groupedEmails.forEach((group) => {
        rows.push({ type: 'header', key: `hdr-${group.label}`, label: group.label });
        group.emails.forEach((email: any) => rows.push({ type: 'email', key: email.id, email }));
      });
    }
    if (isLoadingMore) rows.push({ type: 'loading', key: '__loading__' });
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationView, groupedEmails, emailThreads, isLoadingMore]);

  const virtualListRef = useRef<List>(null);
  const virtualScrollOuterElement = useMemo(() =>
    React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>((props, ref) => (
      <VirtualScrollOuterElement ref={ref} isDark={isDark} {...props} />
    )),
  [isDark]);
  const rowHeightsRef = useRef<Map<string, number>>(new Map());

  const getEstimatedRowHeight = useCallback((row: VirtualRow | undefined) => {
    if (!row) return 50;
    if (row.type === 'header') return HEADER_ROW_HEIGHT;
    if (row.type === 'loading') return LOADING_ROW_HEIGHT;
    // Actual measured card heights (3-line layout: sender/subject/preview + padding):
    // compact=50, cozy=53, comfortable=57. These must match the real rendered card
    // height exactly, otherwise react-window reserves extra space per row and the
    // list shows visible gaps between emails (Gmail has none).
    return isMobile ? 64 : (densityMode === 'compact' ? 50 : densityMode === 'cozy' ? 53 : 57);
  }, [isMobile, densityMode]);

  const getRowHeight = useCallback((index: number) => {
    const row = virtualRows[index];
    if (!row) return 50;
    return rowHeightsRef.current.get(row.key) ?? getEstimatedRowHeight(row);
  }, [virtualRows, getEstimatedRowHeight]);

  const reportRowHeight = useCallback((index: number, key: string, height: number) => {
    const rounded = Math.round(height);
    if (rowHeightsRef.current.get(key) !== rounded) {
      rowHeightsRef.current.set(key, rounded);
      virtualListRef.current?.resetAfterIndex(index);
    }
  }, []);

  // GAP FIX 2026-11-03: clear a row's cached height so react-window falls back to the
  // estimated height. Used when a thread is collapsed so the stale expanded height is
  // dropped and no gap remains below the collapsed thread.
  const clearRowHeight = useCallback((index: number, key: string) => {
    if (rowHeightsRef.current.has(key)) {
      rowHeightsRef.current.delete(key);
      virtualListRef.current?.resetAfterIndex(index);
    }
  }, []);

  const handleItemsRendered = useCallback((props: ListOnItemsRenderedProps) => {
    const { visibleStopIndex } = props;
    // PERF FIX 2026-08-09 (cross-checked against Gmail/Zoho's prefetch-ahead pattern): the
    // previous 5-row threshold meant the fetch for the next page only started once the user
    // had almost visually reached the end of what was rendered, so the network+render latency
    // was fully exposed as a visible stall. Triggering a full page-size (limit) earlier means
    // the next batch is already loading/rendered well before the user scrolls that far,
    // hiding the round-trip behind continued scrolling instead of blocking on it.
    //
    // SPEED FIX 2026-11-02: prefetch a full page ahead (limit) so the next batch is already
    // in-flight well before the user reaches the bottom. Combined with the larger page size
    // (limit=100), this makes continuous scrolling feel instant instead of stalling on each
    // page boundary.
    const prefetchMargin = Math.max(limit, 25);
    if (visibleStopIndex >= virtualRows.length - prefetchMargin && hasMore && !isLoading && !isLoadingMore) {
      loadMoreEmails();
    }
  }, [virtualRows, hasMore, isLoading, isLoadingMore, loadMoreEmails, limit]);

  useEffect(() => {
    // Row identities can shift when the underlying data set changes shape (folder switch,
    // search, category filter) rather than simply appending more pages -- reset cached
    // heights defensively so stale heights from a different folder's rows are never reused.
    rowHeightsRef.current.clear();
    virtualListRef.current?.resetAfterIndex(0, true);
  }, [folder, conversationView, inboxCategory, searchQuery, densityMode]);

  useEffect(() => {
    // Keep reading pane open when folder changes (hybrid 3-pane layout)
    setSelectedEmail(null);
    // Reset category tab when leaving inbox
    if (folder !== 'inbox') setInboxCategory('all');
    // Don't change readingPanePosition - keep it as user preference
    setAutoSplitOpened(false);
    // Clear active search state when navigating to a different folder.
    // IMPORTANT: use clearSearch (not setSearchQuery) to avoid triggering
    // a stale-folder fetch that races with the intended fetchEmails below.
    clearSearch();
    // Demo mode: load mock data only, no API calls
    if (isDemo) { loadDemoEmails(folder); return; }
    // Handle scheduled folder differently
    if (folder === 'scheduled') {
      fetchScheduledEmails();
    } else {
      // Always fetch emails when folder changes or on initial mount
      fetchEmails(folder, 1);
    }
  }, [folder, fetchEmails, fetchScheduledEmails, isDemo, loadDemoEmails]);

  // Polling is handled exclusively by MailboxView to prevent duplicate API calls.
  // EmailListView no longer polls independently.

  const handleSelectAll = () => {
    if (selectedEmails.length === displayEmails.length) {
      clearSelection();
    } else {
      selectAllEmails();
    }
  };

  const handleBulkMarkAsRead = (read: boolean) => {
    if (selectedEmails.length > 0) {
      markAsRead(selectedEmails, read);
    }
  };

  const handleBulkDelete = () => {
    if (selectedEmails.length > 0) {
      deleteEmails(selectedEmails);
      clearSelection();
    }
  };

  const handleBulkArchive = () => {
    if (selectedEmails.length > 0) {
      moveToFolder(selectedEmails, 'archive');
      clearSelection();
    }
  };

  const handleBulkMove = (targetFolder: string) => {
    if (selectedEmails.length > 0) {
      moveToFolder(selectedEmails, targetFolder);
      clearSelection();
    }
    setMoveMenuAnchor(null);
  };

  // Auto-switch to right split on email click (like Zoho)
  const handleEmailClick = (email: any) => {
    if (isMobile) {
      // Mobile: always full-page email reading (Zoho-style)
      setReadingPanePosition('off');
      setAutoSplitOpened(false);
      setSelectedEmail(email);
      fetchEmail(email.id);
      return;
    }
    if (isTablet) {
      // Tablet: use bottom pane to avoid squishing columns
      if (readingPanePosition === 'off') {
        setReadingPanePosition('bottom');
        setAutoSplitOpened(true);
      }
      setSelectedEmail(email);
      fetchEmail(email.id);
      return;
    }
    if (readingPanePosition === 'off') {
      setReadingPanePosition('right');
      setAutoSplitOpened(true);
    }
    setSelectedEmail(email);
    fetchEmail(email.id);
  };

  // Close email detail - if auto-split, return to full list view
  const handleCloseDetail = () => {
    setSelectedEmail(null);
    if (autoSplitOpened) {
      setReadingPanePosition('off');
      setAutoSplitOpened(false);
    }
  };

  // PERF FIX 2026-08-08: memoize react-window's itemData so it's only recreated when a
  // dependency actually changes, instead of a fresh object literal on every single render.
  // react-window relies on itemData REFERENCE equality to decide whether to skip re-rendering
  // already-mounted rows -- an inline object literal defeated that entirely, forcing every
  // visible row to re-render on any unrelated parent re-render (e.g. hover state elsewhere,
  // sticky header label updates while scrolling).
  const virtualItemData = useMemo<VirtualRowData>(() => ({
    rows: virtualRows,
    isDark,
    isMobile,
    isSentFolder: folder === 'sent',
    isArchiveFolder: folder === 'archive',
    selectedEmails,
    selectedEmailId: selectedEmail?.id,
    expandedThreads,
    onToggleExpand: toggleThreadExpand,
    onSelectEmail: (email: any) => handleEmailClick(email),
    onToggleEmailSelection: (id: string) => toggleEmailSelection(id),
    onStarEmail: (email: any) => markAsStarred([email.id], !email.isStarred),
    onSelectThread: (thread: EmailThread) => thread.emails.forEach(e => toggleEmailSelection(e.id)),
    onArchiveEmail: (email: any) => {
      if (email.isScheduled) cancelScheduledEmail(email.id);
      else moveToFolder([email.id], 'archive');
    },
    onUnarchiveEmail: (email: any) => moveToFolder([email.id], 'inbox'),
    onDeleteEmail: (email: any) => {
      if (email.isScheduled) cancelScheduledEmail(email.id);
      else deleteEmails([email.id]);
    },
    reportRowHeight,
    clearRowHeight,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [virtualRows, isDark, isMobile, folder, selectedEmails, selectedEmail?.id, expandedThreads, reportRowHeight, clearRowHeight]);

  const getFolderTitle = () => {
    const titles: Record<string, string> = {
      inbox: 'Inbox',
      drafts: 'Drafts',
      sent: 'Sent',
      scheduled: 'Scheduled',
      spam: 'Spam',
      trash: 'Trash',
      starred: 'Starred',
      important: 'Important',
      snoozed: 'Snoozed',
      templates: 'Templates',
      outbox: 'Outbox',
      home: 'Home',
      'all-unread': 'All Unread',
      unread: 'Unread',
      'all-messages': 'All Messages',
      flagged: 'Flagged',
      archived: 'All Archived',
    };
    if (titles[folder]) return titles[folder];
    const customFolder = storeFolders.find(f => f.id === folder);
    if (customFolder) return customFolder.name;
    return folder.charAt(0).toUpperCase() + folder.slice(1);
  };

  const showReadingPane = readingPanePosition !== 'off';
  const hasSelection = selectedEmails.length > 0;

  // Draggable resize for email list / reading pane split
  // Default: 45% list on desktop, 55% list height on tablet (bottom pane)
  const defaultPct = isTablet ? 55 : 45;
  const [listPanePercent, setListPanePercent] = useState(defaultPct);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = readingPanePosition === 'bottom' ? 'row-resize' : 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (ev: MouseEvent) => {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      let pct: number;
      if (readingPanePosition === 'bottom') {
        pct = ((ev.clientY - rect.top) / rect.height) * 100;
      } else {
        pct = ((ev.clientX - rect.left) / rect.width) * 100;
      }
      // Clamp between 20% and 75%
      pct = Math.max(20, Math.min(75, pct));
      setListPanePercent(pct);
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [readingPanePosition]);

  // When reading pane is off and user clicks an email, show full-page detail
  const isFullPageView = !showReadingPane && selectedEmail;

  // If reading pane is off, show full-page email detail
  if (isFullPageView) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: isDark ? '#0d1117' : '#ffffff' }}>
        {/* Back bar */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          px: isMobile ? 1 : 1.5,
          py: isMobile ? 0.75 : 0.5,
          minHeight: isMobile ? 48 : 'auto',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e0e0e0'}`,
          bgcolor: isDark ? 'transparent' : '#fff',
          gap: 1,
        }}>
          <IconButton 
            size={isMobile ? 'medium' : 'small'}
            onClick={handleCloseDetail}
            sx={{ p: isMobile ? 0.75 : 0.5, color: isDark ? '#9ca3af' : '#5f6368', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0' } }}
          >
            <ArrowBackIcon sx={{ fontSize: isMobile ? 24 : 20 }} />
          </IconButton>
          <Typography sx={{ fontWeight: 600, fontSize: isMobile ? '1rem' : '0.875rem', color: isDark ? '#e5e7eb' : '#333' }}>
            {getFolderTitle()}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {isMobile && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton size="small" onClick={() => { moveToFolder([selectedEmail.id], 'archive'); handleCloseDetail(); }} sx={{ color: isDark ? '#9ca3af' : '#5f6368' }}>
                <ArchiveIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton size="small" onClick={() => { deleteEmails([selectedEmail.id]); handleCloseDetail(); }} sx={{ color: isDark ? '#9ca3af' : '#5f6368' }}>
                <DeleteIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          )}
        </Box>
        {/* Full page email detail */}
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <EmailDetailView email={selectedEmail} onClose={handleCloseDetail} />
        </Box>

        {/* Move Menu */}
        <Menu anchorEl={moveMenuAnchor} open={Boolean(moveMenuAnchor)} onClose={() => setMoveMenuAnchor(null)}>
          <MenuItem onClick={() => handleBulkMove('inbox')}>Inbox</MenuItem>
          <MenuItem onClick={() => handleBulkMove('archive')}>Archive</MenuItem>
          <MenuItem onClick={() => handleBulkMove('spam')}>Spam</MenuItem>
          <MenuItem onClick={() => handleBulkMove('trash')}>Trash</MenuItem>
          <Divider />
          <MenuItem onClick={() => handleBulkMove('drafts')}>Drafts</MenuItem>
          <MenuItem onClick={() => handleBulkMove('sent')}>Sent</MenuItem>
        </Menu>
        <Menu anchorEl={moreMenuAnchor} open={Boolean(moreMenuAnchor)} onClose={() => setMoreMenuAnchor(null)}>
          <MenuItem onClick={() => { handleBulkMarkAsRead(true); setMoreMenuAnchor(null); }}><MarkReadIcon sx={{ mr: 1, fontSize: 20 }} /> Mark all as read</MenuItem>
          <MenuItem onClick={() => { handleBulkMarkAsRead(false); setMoreMenuAnchor(null); }}><MarkUnreadIcon sx={{ mr: 1, fontSize: 20 }} /> Mark all as unread</MenuItem>
        </Menu>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        bgcolor: isDark ? '#0d1117' : '#ffffff',
      }}
    >
      {/* Email List Panel Container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: readingPanePosition === 'bottom' ? 'column' : 'row',
          flexGrow: 1,
          overflow: 'hidden',
        }}
      >
      {/* Email List Panel */}
      <Box
        sx={{
          width: showReadingPane && readingPanePosition === 'right' ? `${listPanePercent}%` : '100%',
          height: showReadingPane && readingPanePosition === 'bottom' ? `${listPanePercent}%` : '100%',
          minWidth: showReadingPane && readingPanePosition === 'right' ? 280 : 'auto',
          flexShrink: 0,
          borderRight: showReadingPane && readingPanePosition === 'right' ? `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e8eaed'}` : 'none',
          borderBottom: showReadingPane && readingPanePosition === 'bottom' ? `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e8eaed'}` : 'none',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: isDark ? '#0d1117' : '#ffffff',
        }}
      >
        {/* Selection Toolbar - shows when items are selected */}
        {hasSelection ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 1,
              py: 0.5,
              borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e8eaed'}`,
              bgcolor: isDark ? 'rgba(26, 115, 232, 0.08)' : '#f2f6fc',
              gap: 0.5,
              minHeight: 48,
            }}
          >
            {/* Checkbox + count */}
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
              <Checkbox
                size="small"
                checked={selectedEmails.length === displayEmails.length}
                indeterminate={selectedEmails.length > 0 && selectedEmails.length < displayEmails.length}
                onChange={handleSelectAll}
                sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: 18 }, color: '#1a73e8', '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#1a73e8' } }}
              />
              <Typography sx={{ fontWeight: 600, color: isDark ? '#93c5fd' : '#1a73e8', fontSize: '0.8125rem', ml: 0.25, whiteSpace: 'nowrap' }}>
                {selectedEmails.length}
              </Typography>
            </Box>

            {/* Divider */}
            <Box sx={{ width: '1px', height: 24, bgcolor: isDark ? 'rgba(255,255,255,0.12)' : '#d0d0d0', flexShrink: 0 }} />

            {/* Archive */}
            <Tooltip title="Archive">
              <IconButton size="small" onClick={handleBulkArchive} sx={{ color: isDark ? '#9ca3af' : '#444746', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#e8eaed' }, borderRadius: '50%' }}>
                <ArchiveIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            {/* Spam / Not spam */}
            {(folder === 'spam' || currentFolder === 'spam') ? (
              <Tooltip title="Not spam">
                <IconButton size="small" onClick={() => { if (selectedEmails.length > 0) { markAsNotSpam(selectedEmails); clearSelection(); } }} sx={{ color: isDark ? '#9ca3af' : '#444746', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#e8eaed' }, borderRadius: '50%' }}>
                  <InboxIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Mark as spam">
                <IconButton size="small" onClick={() => { if (selectedEmails.length > 0) { markAsSpam(selectedEmails); clearSelection(); } }} sx={{ color: isDark ? '#9ca3af' : '#444746', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#e8eaed' }, borderRadius: '50%' }}>
                  <SpamIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}

            {/* Delete */}
            <Tooltip title="Delete">
              <IconButton size="small" onClick={handleBulkDelete} sx={{ color: isDark ? '#9ca3af' : '#444746', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#e8eaed' }, borderRadius: '50%' }}>
                <DeleteIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            {/* Mark as read / unread */}
            <Tooltip title="Mark as read">
              <IconButton size="small" onClick={() => handleBulkMarkAsRead(true)} sx={{ color: isDark ? '#9ca3af' : '#444746', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#e8eaed' }, borderRadius: '50%' }}>
                <MarkReadIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            {/* Move to */}
            <Tooltip title="Move to">
              <IconButton size="small" onClick={(e) => setMoveMenuAnchor(e.currentTarget)} sx={{ color: isDark ? '#9ca3af' : '#444746', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#e8eaed' }, borderRadius: '50%' }}>
                <MoveIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            {/* More (⋮) */}
            <Tooltip title="More">
              <IconButton size="small" onClick={(e) => setMoreMenuAnchor(e.currentTarget)} sx={{ color: isDark ? '#9ca3af' : '#444746', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#e8eaed' }, borderRadius: '50%' }}>
                <MoreVertIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            <Box sx={{ flexGrow: 1 }} />

            {/* Clear selection */}
            <Tooltip title="Clear selection">
              <IconButton size="small" onClick={clearSelection} sx={{ color: isDark ? '#9ca3af' : '#444746', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#e8eaed' }, borderRadius: '50%' }}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          /* Normal Header - Zoho style */
          <Box>
            {/* Folder Title Row */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1.5,
                py: 0.5,
                gap: 1,
                minWidth: 0,
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e0e0e0'}`,
                bgcolor: isDark ? 'transparent' : '#ffffff',
              }}
            >
              {/* Folder heading with checkbox */}
              <Box
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.75, 
                  py: 0.5,
                  px: 0,
                  minWidth: 0,
                  overflow: 'hidden',
                  flexShrink: 1,
                }}
              >
                <Checkbox
                  size="small"
                  checked={selectedEmails.length === displayEmails.length && displayEmails.length > 0}
                  indeterminate={selectedEmails.length > 0 && selectedEmails.length < displayEmails.length}
                  onChange={handleSelectAll}
                  sx={{ 
                    p: 0.25, 
                    '& .MuiSvgIcon-root': { fontSize: 18 }, 
                    color: isDark ? '#4b5563' : '#bbb',
                    '&.Mui-checked': { color: '#1561e8' },
                    '&.MuiCheckbox-indeterminate': { color: '#1561e8' },
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{ 
                    fontWeight: 500, 
                    color: isDark ? '#e5e7eb' : '#202124', 
                    fontSize: '0.875rem',
                    letterSpacing: '-0.01em',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {getFolderTitle()}
                </Typography>
{(() => {
                  const folderData = storeFolders.find(f => f.id === folder);
                  const unread = folderData?.unreadCount ?? 0;
                  // Fall back to pagination total for the currently loaded folder
                  const displayTotal = unread > 0 ? unread : (currentFolder === folder ? total : folderData?.totalCount ?? 0);
                  if (!displayTotal) return null;
                  const label = unread > 0
                    ? `${unread.toLocaleString()} Unread ${unread === 1 ? 'email' : 'emails'}`
                    : `${displayTotal.toLocaleString()} ${displayTotal === 1 ? 'email' : 'emails'}`;
                  return (
                    <>
                      <Typography sx={{ color: isDark ? '#4b5563' : '#bdbdbd', fontSize: '0.75rem', mx: 0.5, lineHeight: 1, userSelect: 'none', flexShrink: 0 }}>·</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: isDark ? '#6b7280' : '#5f6368', fontWeight: 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {label}
                      </Typography>
                    </>
                  );
                })()}
              </Box>

              {/* Right side view toggle icons */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                {/* Gmail-style Pagination - show when there are emails */}
                {total > 0 && (
                  <>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: isDark ? '#9ca3af' : '#5f6368',
                        fontSize: '0.75rem',
                        mr: 0.5,
                        userSelect: 'none',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {/* With infinite scroll, "emails" accumulates across loadMoreEmails calls,
                          so show the actual loaded count rather than page*limit math which
                          only applies to the classic replace-on-click Prev/Next flow. */}
                      {`1-${Math.min(emails.length, total)} of ${total}`}
                    </Typography>
                    <Tooltip title="Older">
                      <span>
                        <IconButton
                          size="small"
                          disabled={page === 1 || isLoading}
                          onClick={() => prevPage()}
                          sx={{ 
                            p: 0.5, 
                            color: isDark ? '#6b7280' : '#5f6368',
                            '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0' },
                            '&.Mui-disabled': { 
                              color: isDark ? '#374151' : '#dadce0',
                              opacity: 0.4
                            }
                          }}
                        >
                          <PrevIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Newer">
                      <span>
                        <IconButton
                          size="small"
                          disabled={!hasMore || isLoading}
                          onClick={() => nextPage()}
                          sx={{ 
                            p: 0.5, 
                            color: isDark ? '#6b7280' : '#5f6368',
                            '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0' },
                            '&.Mui-disabled': { 
                              color: isDark ? '#374151' : '#dadce0',
                              opacity: 0.4
                            }
                          }}
                        >
                          <NextIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Box sx={{ width: '1px', height: '20px', bgcolor: isDark ? 'rgba(255,255,255,0.1)' : '#e0e0e0', mx: 0.5 }} />
                  </>
                )}

                {/* Refresh */}
                <Tooltip title="Refresh">
                  <IconButton
                    size="small"
                    onClick={() => syncEmails()}
                    sx={{ p: 0.5, color: isDark ? '#6b7280' : '#999', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0' } }}
                  >
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>

                {/* Density - hidden on mobile */}
                {!isMobile && (
                <Tooltip title={`Density: ${densityMode}`}>
                  <IconButton
                    size="small"
                    onClick={(e) => setDensityMenuAnchor(e.currentTarget)}
                    sx={{ p: 0.5, color: isDark ? '#6b7280' : '#999', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0' } }}
                  >
                    {densityMode === 'compact' ? <ViewHeadlineIcon sx={{ fontSize: 18 }} /> : densityMode === 'cozy' ? <ViewStreamIcon sx={{ fontSize: 18 }} /> : <ViewAgendaIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </Tooltip>
                )}

                {/* Reading Pane Position - hidden on mobile */}
                {!isMobile && (
                <Tooltip title={`Reading pane: ${readingPanePosition}`}>
                  <IconButton
                    size="small"
                    onClick={(e) => setViewMenuAnchor(e.currentTarget)}
                    sx={{ p: 0.5, color: isDark ? '#6b7280' : '#999', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0' } }}
                  >
                    {readingPanePosition === 'right' ? <VerticalSplitIcon sx={{ fontSize: 18 }} /> : readingPanePosition === 'bottom' ? <HorizontalSplitIcon sx={{ fontSize: 18 }} /> : <NoSplitIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </Tooltip>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* Search scope/sort chips — shown when a search is active */}
        {!hasSelection && searchQuery && (
          <SearchScopeBar
            currentFolder={folder}
            searchQuery={searchQuery}
          />
        )}

        {/* Smart Category Tabs — inbox only, not during search */}
        {!hasSelection && folder === 'inbox' && !searchQuery && (
          <Tabs
            value={inboxCategory}
            onChange={(_, v) => setInboxCategory(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 36,
              borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e8eaed'}`,
              bgcolor: isDark ? 'transparent' : '#fff',
              '& .MuiTabs-indicator': { height: 2, bgcolor: '#1a73e8', borderRadius: '2px 2px 0 0' },
              '& .MuiTab-root': {
                minHeight: 36,
                py: 0,
                px: 1.5,
                fontSize: '0.875rem',
                fontWeight: 500,
                textTransform: 'none',
                color: isDark ? '#9ca3af' : '#5f6368',
                letterSpacing: 0,
                gap: 0.5,
                '&.Mui-selected': { color: '#1a73e8', fontWeight: 600 },
              },
            }}
          >
            <Tab value="all" label="All Mail" icon={<MailOutlineIcon sx={{ fontSize: 15 }} />} iconPosition="start" />
            <Tab value="primary" label="Primary" icon={<InboxCatIcon sx={{ fontSize: 15 }} />} iconPosition="start" />
            <Tab value="social" label="Social" icon={<PeopleIcon sx={{ fontSize: 15 }} />} iconPosition="start" />
            <Tab value="promotions" label="Promotions" icon={<PromoIcon sx={{ fontSize: 15 }} />} iconPosition="start" />
            <Tab value="updates" label="Updates" icon={<UpdatesIcon sx={{ fontSize: 15 }} />} iconPosition="start" />
            <Tab value="forums" label="Forums" icon={<ForumsIcon sx={{ fontSize: 15 }} />} iconPosition="start" />
          </Tabs>
        )}

        {/* Loading indicator */}
        {isLoading && <LinearProgress sx={{ height: 2, bgcolor: isDark ? 'rgba(59,130,246,0.1)' : 'rgba(26,115,232,0.1)' }} />}

        {/* Email List */}
        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            position: 'relative',
            bgcolor: isDark ? 'transparent' : '#ffffff',
          }}
        >
          {displayEmails.length === 0 && !isLoading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                p: 4,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: isDark ? 'rgba(96, 165, 250, 0.08)' : '#f0f4ff',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <ArchiveIcon sx={{ fontSize: 36, color: isDark ? '#60a5fa' : '#1561e8', opacity: 0.5 }} />
              </Box>
              <Typography variant="body1" sx={{ color: isDark ? '#e0e0e0' : '#333', fontWeight: 500, mb: 0.5, fontSize: '0.9375rem' }}>
                No emails in {getFolderTitle()}
              </Typography>
              <Typography variant="body2" sx={{ color: isDark ? '#999' : '#999', fontSize: '0.8125rem' }}>
                Messages will appear here
              </Typography>
            </Box>
          ) : (
            <>
              <AutoSizer>
                {({ height, width }: { height: number; width: number }) => (
                  <List
                    ref={virtualListRef}
                    height={height}
                    width={width}
                    itemCount={virtualRows.length}
                    itemSize={getRowHeight}
                    estimatedItemSize={isMobile ? 64 : 68}
                    overscanCount={6}
                    onItemsRendered={handleItemsRendered}
                    itemData={virtualItemData}
                    outerElementType={virtualScrollOuterElement}
                  >
                    {VirtualizedRow}
                  </List>
                )}
              </AutoSizer>
            </>
          )}
        </Box>


      </Box>

      {/* Draggable Resize Handle */}
      {showReadingPane && (
        <Box
          onMouseDown={handleResizeStart}
          sx={{
            width: readingPanePosition === 'right' ? '8px' : '100%',
            height: readingPanePosition === 'bottom' ? '8px' : '100%',
            cursor: readingPanePosition === 'bottom' ? 'row-resize' : 'col-resize',
            bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#f0f0f0',
            flexShrink: 0,
            position: 'relative',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              bgcolor: isDark ? 'rgba(96,165,250,0.25)' : 'rgba(21,97,232,0.12)',
              '& .resize-grip': { opacity: 1 },
            },
            '&:active': {
              bgcolor: isDark ? 'rgba(96,165,250,0.4)' : 'rgba(21,97,232,0.22)',
            },
          }}
        >
          {/* Grip dots indicator */}
          <Box
            className="resize-grip"
            sx={{
              opacity: 0.4,
              display: 'flex',
              flexDirection: readingPanePosition === 'right' ? 'column' : 'row',
              gap: '3px',
              pointerEvents: 'none',
            }}
          >
            {[0,1,2].map(i => (
              <Box key={i} sx={{
                width: readingPanePosition === 'right' ? '3px' : '3px',
                height: readingPanePosition === 'right' ? '3px' : '3px',
                borderRadius: '50%',
                bgcolor: isDark ? '#9ca3af' : '#9e9e9e',
              }} />
            ))}
          </Box>
        </Box>
      )}

      {/* Reading Pane */}
      {showReadingPane && (
        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,
            height: readingPanePosition === 'bottom' ? `${100 - listPanePercent}%` : '100%',
            overflow: 'auto',
            bgcolor: selectedEmail ? theme.palette.background.paper : (isDark ? '#111827' : '#f8f9fa'),
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {selectedEmail ? (
            <EmailDetailView email={selectedEmail} onClose={handleCloseDetail} />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: isDark ? '#9ca3af' : '#5f6368',
              }}
            >
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: isDark ? '#374151' : '#e8eaed',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <MarkReadIcon sx={{ fontSize: 40, color: '#9aa0a6' }} />
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                Select an email to read
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click on an email from the list to view its contents
              </Typography>
            </Box>
          )}
        </Box>
      )}
      </Box>

      {/* Move Menu */}
      <Menu
        anchorEl={moveMenuAnchor}
        open={Boolean(moveMenuAnchor)}
        onClose={() => setMoveMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleBulkMove('inbox')}>Inbox</MenuItem>
        <MenuItem onClick={() => handleBulkMove('archive')}>Archive</MenuItem>
        <MenuItem onClick={() => handleBulkMove('spam')}>Spam</MenuItem>
        <MenuItem onClick={() => handleBulkMove('trash')}>Trash</MenuItem>
        <Divider />
        <MenuItem onClick={() => handleBulkMove('drafts')}>Drafts</MenuItem>
        <MenuItem onClick={() => handleBulkMove('sent')}>Sent</MenuItem>
      </Menu>

      {/* More Actions Menu */}
      <Menu
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={() => setMoreMenuAnchor(null)}
        PaperProps={{ sx: { minWidth: 220, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' } }}
      >
        <MenuItem onClick={() => { setMoreMenuAnchor(null); }} sx={{ fontSize: '0.875rem', gap: 1.5 }}>
          <SnoozeIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> Snooze
        </MenuItem>
        <MenuItem onClick={() => { setMoreMenuAnchor(null); }} sx={{ fontSize: '0.875rem', gap: 1.5 }}>
          <AddToTasksIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> Add to Tasks
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { markAsStarred(selectedEmails, true); setMoreMenuAnchor(null); }} sx={{ fontSize: '0.875rem', gap: 1.5 }}>
          <StarIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> Star selected
        </MenuItem>
        <MenuItem
          onClick={(e) => { setLabelMenuAnchor(e.currentTarget); setLabelSearch(''); }}
          sx={{ fontSize: '0.875rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LabelAsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            Label as
          </Box>
          <ChevronRightIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { handleBulkMarkAsRead(true); setMoreMenuAnchor(null); }} sx={{ fontSize: '0.875rem', gap: 1.5 }}>
          <MarkReadIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> Mark as read
        </MenuItem>
        <MenuItem onClick={() => { handleBulkMarkAsRead(false); setMoreMenuAnchor(null); }} sx={{ fontSize: '0.875rem', gap: 1.5 }}>
          <MarkUnreadIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> Mark as unread
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { setMoreMenuAnchor(null); }} sx={{ fontSize: '0.875rem', gap: 1.5 }}>
          <ForwardAttachmentIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> Forward as attachment
        </MenuItem>
        <MenuItem onClick={() => { setMoreMenuAnchor(null); }} sx={{ fontSize: '0.875rem', gap: 1.5 }}>
          <FilterMessagesIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> Filter messages like these
        </MenuItem>
        <MenuItem onClick={() => { setMoreMenuAnchor(null); }} sx={{ fontSize: '0.875rem', gap: 1.5 }}>
          <MuteIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> Mute
        </MenuItem>
      </Menu>

      {/* Label As Submenu */}
      <Menu
        anchorEl={labelMenuAnchor}
        open={Boolean(labelMenuAnchor)}
        onClose={() => setLabelMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            minWidth: 240,
            maxWidth: 280,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
            overflow: 'visible',
          },
        }}
      >
        {/* Search box */}
        <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Search labels"
            value={labelSearch}
            onChange={(e) => setLabelSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: 1, fontSize: '0.875rem' },
            }}
          />
        </Box>
        <Divider sx={{ my: 0.5 }} />
        {/* Label list */}
        <Box sx={{ maxHeight: 240, overflowY: 'auto' }}>
          {(labels.length === 0
            ? [{ id: 'work', name: 'Work', color: '#ea4335' }, { id: 'personal', name: 'Personal', color: '#34a853' }, { id: 'important', name: 'Important', color: '#fbbc04' }]
            : labels
          )
            .filter((l) => l.name.toLowerCase().includes(labelSearch.toLowerCase()))
            .map((label) => {
              const isApplied = selectedEmails.some((id) => {
                const email = emails.find((e) => e.id === id);
                return email?.labels?.includes(label.id);
              });
              return (
                <MenuItem
                  key={label.id}
                  onClick={() => {
                    if (isApplied) {
                      removeLabel(selectedEmails, label.id);
                    } else {
                      addLabel(selectedEmails, label.id);
                    }
                    setLabelMenuAnchor(null);
                    setMoreMenuAnchor(null);
                  }}
                  sx={{ fontSize: '0.875rem', py: 0.75 }}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Checkbox
                      size="small"
                      checked={isApplied}
                      disableRipple
                      sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 16 } }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: label.color, flexShrink: 0 }} />
                        {label.name}
                      </Box>
                    }
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                  />
                </MenuItem>
              );
            })}
          {labels.length > 0 && labels.filter((l) => l.name.toLowerCase().includes(labelSearch.toLowerCase())).length === 0 && (
            <Box sx={{ px: 2, py: 1.5, color: 'text.disabled', fontSize: '0.8rem', textAlign: 'center' }}>No labels found</Box>
          )}
        </Box>
        <Divider sx={{ mt: 0.5 }} />
        {/* Footer actions */}
        <MenuItem
          onClick={() => { setLabelMenuAnchor(null); setMoreMenuAnchor(null); /* TODO: open create label dialog */ }}
          sx={{ fontSize: '0.875rem', gap: 1.5, py: 0.75 }}
        >
          <AddLabelIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          Create new
        </MenuItem>
        <MenuItem
          onClick={() => { setLabelMenuAnchor(null); setMoreMenuAnchor(null); /* navigate to labels settings */ }}
          sx={{ fontSize: '0.875rem', gap: 1.5, py: 0.75 }}
        >
          <ManageLabelsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          Manage labels
        </MenuItem>
      </Menu>

      {/* Density Menu */}
      <Menu
        anchorEl={densityMenuAnchor}
        open={Boolean(densityMenuAnchor)}
        onClose={() => setDensityMenuAnchor(null)}
        PaperProps={{ sx: { minWidth: 180 } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: isDark ? '#e0e0e0' : '#333', mb: 0.5 }}>
            List Density
          </Typography>
        </Box>
        <MenuItem
          selected={densityMode === 'comfortable'}
          onClick={() => { setDensityMode('comfortable'); setDensityMenuAnchor(null); }}
          sx={{ fontSize: '0.84rem' }}
        >
          <ViewAgendaIcon sx={{ mr: 1.5, fontSize: 18, color: densityMode === 'comfortable' ? '#1a73e8' : (isDark ? '#999' : '#666') }} />
          Comfortable
        </MenuItem>
        <MenuItem
          selected={densityMode === 'cozy'}
          onClick={() => { setDensityMode('cozy'); setDensityMenuAnchor(null); }}
          sx={{ fontSize: '0.84rem' }}
        >
          <ViewStreamIcon sx={{ mr: 1.5, fontSize: 18, color: densityMode === 'cozy' ? '#1a73e8' : (isDark ? '#999' : '#666') }} />
          Cozy
        </MenuItem>
        <MenuItem
          selected={densityMode === 'compact'}
          onClick={() => { setDensityMode('compact'); setDensityMenuAnchor(null); }}
          sx={{ fontSize: '0.84rem' }}
        >
          <ViewHeadlineIcon sx={{ mr: 1.5, fontSize: 18, color: densityMode === 'compact' ? '#1a73e8' : (isDark ? '#999' : '#666') }} />
          Compact
        </MenuItem>
      </Menu>

      {/* Reading Pane / View Menu */}
      <Menu
        anchorEl={viewMenuAnchor}
        open={Boolean(viewMenuAnchor)}
        onClose={() => setViewMenuAnchor(null)}
        PaperProps={{ sx: { minWidth: 200 } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: isDark ? '#e0e0e0' : '#333', mb: 0.5 }}>
            Reading Pane
          </Typography>
        </Box>
        <MenuItem
          selected={readingPanePosition === 'right' || autoSplitOpened}
          onClick={() => { setReadingPanePosition('right'); setAutoSplitOpened(false); setViewMenuAnchor(null); }}
          sx={{ fontSize: '0.84rem' }}
        >
          <VerticalSplitIcon sx={{ mr: 1.5, fontSize: 18, color: readingPanePosition === 'right' ? '#1a73e8' : (isDark ? '#999' : '#666') }} />
          Right
        </MenuItem>
        <MenuItem
          selected={readingPanePosition === 'bottom'}
          onClick={() => { setReadingPanePosition('bottom'); setAutoSplitOpened(false); setViewMenuAnchor(null); }}
          sx={{ fontSize: '0.84rem' }}
        >
          <HorizontalSplitIcon sx={{ mr: 1.5, fontSize: 18, color: readingPanePosition === 'bottom' ? '#1a73e8' : (isDark ? '#999' : '#666') }} />
          Bottom
        </MenuItem>
        <MenuItem
          selected={readingPanePosition === 'off' && !autoSplitOpened}
          onClick={() => { setReadingPanePosition('off'); setAutoSplitOpened(false); setSelectedEmail(null); setViewMenuAnchor(null); }}
          sx={{ fontSize: '0.84rem' }}
        >
          <NoSplitIcon sx={{ mr: 1.5, fontSize: 18, color: readingPanePosition === 'off' ? '#1a73e8' : (isDark ? '#999' : '#666') }} />
          Off
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default EmailListView;
